import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { RewardMenuRewardIcon, RewardMenuGoalIcon, type RewardMenuIconProps } from '../../icons'
import type { RewardDropdownContentProps, RewardMenuAction } from './RewardDropdown.types'
import './RewardDropdown.scss'

const ITEMS: {
  action: RewardMenuAction
  labelKey: string
  shortcut: string
  Icon: React.FC<RewardMenuIconProps>
}[] = [
  { action: 'reward', labelKey: 'taskCreation.rewardMenu.addReward', shortcut: '1', Icon: RewardMenuRewardIcon },
  { action: 'goal', labelKey: 'taskCreation.rewardMenu.myGoal', shortcut: '2', Icon: RewardMenuGoalIcon },
]

/**
 * Content of the "Motivieren" dropdown (Figma 3324-43990) — the gift button in
 * the task action bar. Two entries: attach a reward, or name the goal behind
 * the task. Rendered inside the DropdownMenuContent managed by
 * `TaskActionButton`, same as the attention / duration / tag dropdowns.
 *
 * Keyboard: `1` and `2` pick an entry directly while the menu is open — unless
 * `shortcuts` is off, as in the Aufgaben-Menü.
 */
export const RewardDropdownContent: React.FC<RewardDropdownContentProps> = ({
  onSelect,
  onClose,
  placement = 'actionbar',
  shortcuts = true,
}) => {
  const { t } = useTranslation()

  const handleSelect = useCallback((action: RewardMenuAction) => {
    onSelect(action)
    onClose()
  }, [onSelect, onClose])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!shortcuts) return
    const item = ITEMS.find(entry => entry.shortcut === e.key)
    if (!item) return
    e.preventDefault()
    e.stopPropagation()
    handleSelect(item.action)
  }, [handleSelect, shortcuts])

  return (
    // Key handling sits on the wrapper (not the buttons) so 1/2 work anywhere
    // in the open menu — same as AttentionDropdown.
    <div className="reward-dropdown" onKeyDown={handleKeyDown} data-placement={placement}>
      {ITEMS.map(({ action, labelKey, shortcut, Icon }) => (
        <button
          key={action}
          type="button"
          className="context-menu-content__item reward-dropdown__item"
          onClick={() => handleSelect(action)}
        >
          {/* 20px instead of the 28px in Figma: the other context menus run on
              16px icons, and a 28px emoji would make this menu's rows visibly
              taller. 20px + the negative block margin in SCSS keeps the row at
              the shared 28px height while the emoji stays readable. */}
          <Icon size={20} className="reward-dropdown__icon" />
          {t(labelKey)}
          {shortcuts && <ShortcutHint className="reward-dropdown__shortcut">{shortcut}</ShortcutHint>}
        </button>
      ))}
    </div>
  )
}
